import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { Bookmark } from 'lucide-react';
import { useAuth } from '../App';
import { toast } from 'sonner';

const API = '/api';

/**
 * SaveButton — reusable save/bookmark toggle for any content type.
 *
 * Props:
 *   targetId     string   — ID of the content item
 *   targetType   string   — 'artwork' | 'series' | 'episode' | 'novel'
 *   initialSaved boolean  — whether the current user has saved this item
 *   saveCount    number   — current save count (for optional display)
 *   showCount    boolean  — show save count next to button
 *   size         string   — 'sm' | 'md'
 *   className    string   — additional classes
 */
const SaveButton = ({
  targetId,
  targetType,
  initialSaved = false,
  saveCount: initialCount = 0,
  showCount = false,
  size = 'sm',
  className = '',
}) => {
  const { user } = useAuth();
  const [saved, setSaved] = useState(initialSaved);
  const [count, setCount] = useState(initialCount);
  const [pending, setPending] = useState(false);

  const handleToggle = useCallback(
    async (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!user) {
        toast.error('Please sign in to save');
        return;
      }
      if (pending) return;

      // Optimistic update
      const wasSaved = saved;
      setSaved(!wasSaved);
      setCount((c) => (wasSaved ? Math.max(0, c - 1) : c + 1));
      setPending(true);

      try {
        const token = localStorage.getItem('token');
        const res = await axios.post(
          `${API}/saves/toggle`,
          { target_id: targetId, target_type: targetType },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        setSaved(res.data.saved);
        setCount(res.data.save_count);
      } catch (err) {
        // Revert optimistic update
        setSaved(wasSaved);
        setCount((c) => (wasSaved ? c + 1 : Math.max(0, c - 1)));
        toast.error(err.response?.data?.detail || 'Failed to save');
      } finally {
        setPending(false);
      }
    },
    [user, saved, pending, targetId, targetType],
  );

  const iconSize = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';

  return (
    <button
      onClick={handleToggle}
      disabled={pending}
      className={`inline-flex items-center gap-1 transition-all duration-200 disabled:opacity-60 ${
        saved
          ? 'text-orange-400 hover:text-orange-300'
          : 'text-zinc-500 hover:text-zinc-300'
      } ${className}`}
      title={saved ? 'Unsave' : 'Save'}
      data-testid={`save-btn-${targetId}`}
      aria-label={saved ? 'Unsave' : 'Save'}
      aria-pressed={saved}
    >
      <Bookmark
        className={`${iconSize} transition-transform duration-200 ${saved ? 'fill-current scale-110' : ''} ${pending ? 'animate-pulse' : ''}`}
      />
      {showCount && count > 0 && (
        <span className={`${size === 'sm' ? 'text-xs' : 'text-sm'}`}>{count}</span>
      )}
    </button>
  );
};

export default SaveButton;
